import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DataConnection } from '@/apis/data_connectors/types';
import { TeamDataConnection } from '@/apis/teams/data_connections/api';
import {
  getTeamDataConnections,
  shareConnectionWithTeam,
  unshareConnectionWithTeam
} from '@/apis/teams/data_connections/api';
import { useCurrentTeam } from '@/apis/teams/hooks';

export const useTeamDataConnections = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const { data: currentTeam } = useCurrentTeam();

  const teamId = currentTeam?.details?.id;

  return useQuery<DataConnection[], Error>({
    queryKey: ['team_data_connections', teamId],
    enabled: isAuthenticated && !!user && !!teamId,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getTeamDataConnections({ teamId, accessToken });
    },
    retry: 3,
    refetchOnWindowFocus: false
  });
};

interface ToggleConnectionArgs {
  connectionId: string;
  currentlyShared: boolean;
}

export const useToggleConnectionShareStatus = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { data: currentTeam } = useCurrentTeam();
  const teamId = currentTeam?.details?.id;

  return useMutation<TeamDataConnection | { success: boolean }, Error, ToggleConnectionArgs>({
    mutationFn: async ({ connectionId, currentlyShared }) => {
      const accessToken = await getAccessTokenSilently();

      if (currentlyShared) {
        return unshareConnectionWithTeam({ teamId, connectionId, accessToken });
      }

      return shareConnectionWithTeam({ teamId, connectionId, accessToken });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team_data_connections', teamId] });
    }
  });
};
